import React, { useId, useMemo } from 'react';
import { AppIcon } from '../AppIcon';
import { iconService } from '../../services/iconService';
import { HEALTH_APP_ICONS } from './healthAppIcons';
import './AppSquircle.scss';

export type AppSquircleSize = 'sm' | 'md' | 'lg';

interface AppSquircleProps {
  appId: string;
  size?: AppSquircleSize;
  className?: string;
}

// Icon pixel size inside the squircle for each size variant
const ICON_PX: Record<AppSquircleSize, number> = {
  sm: 12,
  md: 20,
  lg: 28,
};

// Physician glyph size — leaves enough of the background visible
const PHYSICIAN_GLYPH_PX: Record<AppSquircleSize, number> = {
  sm: 13,
  md: 20,
  lg: 28,
};

// Full container size for passthrough (image) icons
const CONTAINER_PX: Record<AppSquircleSize, number> = {
  sm: 18,
  md: 32,
  lg: 46,
};

// Dark green that reads cleanly on the mint-green svgbase gradient
const PHYSICIAN_GLYPH_COLOR = '#0A5F35';

/**
 * Inline SVG base for physician apps — the shared svgbase.svg design:
 * dark background, white-to-mint-green radial gradient, subtle green wave.
 * Uses React.useId() for unique defs IDs so multiple instances don't collide.
 */
const PhysicianSquircleBase: React.FC<{ containerPx: number }> = ({ containerPx }) => {
  const uid = useId().replace(/:/g, '');
  const gradId = `phys-g-${uid}`;
  const clipId = `phys-c-${uid}`;

  return (
    <svg
      width={containerPx}
      height={containerPx}
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="app-squircle__physician-base"
      aria-hidden="true"
    >
      <g clipPath={`url(#${clipId})`}>
        <rect width="512" height="512" rx="120" fill="#111111" />
        <rect width="512" height="512" rx="120" fill={`url(#${gradId})`} />
        <path
          d="M256 335.203C137.576 250.978 71.8907 250.887 -44 335.203V509.5H556V335.203C440.632 412.817 375.484 411.712 256 335.203Z"
          fill="#0DB564"
          fillOpacity="0.2"
        />
      </g>
      <defs>
        <radialGradient
          id={gradId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(256 82.3729) rotate(90) scale(429.627 508.891)"
        >
          <stop offset="0.105769" stopColor="#F2FFF2" />
          <stop offset="1" stopColor="#B5F6C6" />
        </radialGradient>
        <clipPath id={clipId}>
          <rect width="512" height="512" rx="120" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

/**
 * Renders an app icon inside a colored squircle (superellipse) background.
 *
 * Three visual modes:
 *
 * 1. Passthrough — image/svg icons (Drive / riversystem.svg) carry their own
 *    background; no squircle gradient or clip-path modification.
 *
 * 2. Physician — apps whose appId starts with 'phys-': renders the shared
 *    svgbase.svg design with the healthicon or Phosphor glyph centered on top
 *    in dark green, sized to let the background show through.
 *
 * 3. Gradient — default for all other apps: tinted squircle, icon stripped to
 *    white so it reads cleanly against the colored background.
 */
export const AppSquircle: React.FC<AppSquircleProps> = ({ appId, size = 'sm', className = '' }) => {
  const px = ICON_PX[size];
  const glyphPx = PHYSICIAN_GLYPH_PX[size];
  const containerPx = CONTAINER_PX[size];

  const healthEntry = HEALTH_APP_ICONS[appId];
  const isPhysicianApp = appId.startsWith('phys-');

  // Resolve icon config up front — hooks must always run unconditionally.
  const iconConfig = useMemo(() => {
    if (healthEntry) {
      return {
        type: 'component' as const,
        component: healthEntry.component,
        color: PHYSICIAN_GLYPH_COLOR,
        // Pass explicit dimensions so the component renders at the right size
        componentProps: { width: glyphPx, height: glyphPx },
        metadata: { source: 'app' as const, appId, isAppIcon: false },
      };
    }
    return iconService.getAppIcon(appId);
  }, [appId, healthEntry, glyphPx]);

  // Gradient variant: icon stripped of color so it renders white on the tinted bg.
  const iconForSquircle = useMemo(() => ({
    ...iconConfig,
    color: undefined,
    componentProps: iconConfig.type === 'component' ? { width: px, height: px } : undefined,
    metadata: { ...iconConfig.metadata, isAppIcon: false },
  }), [iconConfig, px]);

  // --- Passthrough: image/svg icons (e.g. Drive/riversystem.svg) ---
  const isPassthrough = iconConfig.type === 'image' || iconConfig.type === 'svg';

  if (isPassthrough) {
    return (
      <span className={`app-squircle__outer ${className}`}>
        <span className={`app-squircle app-squircle--passthrough app-squircle--${size}`}>
          <AppIcon icon={iconConfig} width={containerPx} height={containerPx} />
        </span>
      </span>
    );
  }

  // --- Physician: svgbase.svg design + glyph centered on top ---
  if (isPhysicianApp) {
    // For phosphor icons (e.g. phys-icd10x), override color; component icons
    // already have glyphPx set via componentProps above.
    // isAppIcon must be false so AppIcon uses raw entity-icon styling (no chiplet
    // badge background/border-radius around the glyph).
    const glyphIcon = {
      ...iconConfig,
      color: PHYSICIAN_GLYPH_COLOR,
      componentProps: { width: glyphPx, height: glyphPx },
      metadata: { ...iconConfig.metadata, isAppIcon: false as const },
    };
    return (
      <span className={`app-squircle__outer`}>
      <span
        className={`app-squircle app-squircle--physician app-squircle--${size} ${className}`}
        style={{ width: containerPx, height: containerPx } as React.CSSProperties}
      >
        <PhysicianSquircleBase containerPx={containerPx} />
        <span className="app-squircle__physician-glyph">
          <AppIcon icon={glyphIcon} width={glyphPx} height={glyphPx} />
        </span>
      </span>
      </span>
    );
  }

  // --- Gradient: default — colored squircle, icon stripped to white ---
  const appColor = (iconConfig as any).color || '#6366f1';

  // The filter (drop-shadow) lives on the outer wrapper so clip-path on the
  // inner element doesn't clip the shadow. CSS renders filter then clip-path
  // on the same element, which would cut the shadow off.
  return (
    <span
      className={`app-squircle__outer ${className}`}
      style={{ '--app-color': appColor } as React.CSSProperties}
    >
      <span className={`app-squircle app-squircle--${size}`}>
        <AppIcon icon={iconForSquircle} width={px} height={px} />
      </span>
    </span>
  );
};

export default AppSquircle;
